/**
 * VersionChip — three SHA badges (chanakya / prajna / forecast-svc).
 *
 * Useful when chasing "did my fix actually deploy?" questions. Click
 * to copy a one-line summary; hover to see the full payload (deploy
 * timestamp, pod name, k8s revision).
 *
 * SHAs:
 *   • chanakya — VITE_BUILD_SHA, baked at FE build time
 *   • prajna   — BUILD_GIT_SHA from /api/version/ (kubectl set env on deploy)
 *   • forecast-svc — BUILD_GIT_SHA via prajna's /api/version/ proxy
 *
 * Failure modes are surfaced ("?" + tooltip) rather than hidden so the
 * planner knows the chip itself didn't fall over silently.
 */
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";

const FE_SHA = (import.meta.env.VITE_BUILD_SHA as string | undefined)?.slice(0, 7) || "dev";

interface VersionPayload {
  prajna: { git_sha: string; k8s_pod?: string };
  forecast_svc: {
    git_sha: string; k_revision?: string; error?: string | null;
    /** The BE served its last good answer because the service is asleep. */
    cached?: boolean; note?: string | null;
  };
}

function shortSha(sha?: string): string {
  if (!sha) return "—";
  return sha.length > 7 ? sha.slice(0, 7) : sha;
}

export function VersionChip() {
  const [payload, setPayload] = useState<VersionPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const apiBase = (import.meta.env.VITE_API_BASE_URL as string)
      || "http://localhost:8000/api";
    fetch(`${apiBase}/public/version/`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((p: VersionPayload) => setPayload(p))
      .catch((e) => setErr(e.message || "fetch failed"));
  }, []);

  const prajnaSha = shortSha(payload?.prajna?.git_sha);
  const fcSha = shortSha(payload?.forecast_svc?.git_sha);

  const oneLine = [
    `FE ${FE_SHA}`,
    `prajna ${prajnaSha}`,
    `forecast-svc ${fcSha}`,
  ].join(" · ");

  const copy = () => {
    navigator.clipboard.writeText(oneLine).then(
      () => toast.success("Version copied to clipboard"),
      () => toast.error("Couldn't copy version"),
    );
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={copy}
            className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground hover:text-foreground transition-colors"
            aria-label="App versions"
          >
            <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
              FE <span className="ml-1 text-foreground">{FE_SHA}</span>
            </Badge>
            <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
              BE <span className="ml-1 text-foreground">{prajnaSha}</span>
            </Badge>
            <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
              FCS <span className="ml-1 text-foreground">{fcSha}</span>
            </Badge>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="end" className="text-xs max-w-xs">
          <div className="space-y-1 font-mono">
            <div>FE: {FE_SHA}</div>
            <div>prajna: {prajnaSha}</div>
            <div>forecast-svc: {fcSha}</div>
            {payload?.prajna?.k8s_pod && (
              <div className="text-muted-foreground">pod: {payload.prajna.k8s_pod}</div>
            )}
            {payload?.forecast_svc?.error && (
              <div className="text-destructive">
                forecast-svc: {payload.forecast_svc.error}
              </div>
            )}
            {payload?.forecast_svc?.note && (
              <div className="text-muted-foreground">
                forecast-svc: {payload.forecast_svc.note}
              </div>
            )}
            {err && <div className="text-destructive">version: {err}</div>}
            <div className="text-muted-foreground pt-1 border-t mt-1">Click to copy</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
